import Box from '@mui/material/Box';
import Slider, { SliderThumb } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import { useWindowWidth } from '@react-hook/window-size';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import { homeStyles } from '..';
import { ReactComponent as RoosterGold } from '../../../assets/Rooster-Gold.svg';
import { ReactComponent as Rooster } from '../../../assets/Rooster-White.svg';
import { uicolors } from '../../../theme';
import { captureError } from '../../../utils/capture-error';
import { isAgt } from '../../../utils/is-agent';

export function ContestTracker(props: { AgtNo: string; isMobile: boolean }) {
  // Props
  const { AgtNo, isMobile } = props;
  // Hooks
  const width = useWindowWidth();
  // State
  const maxPoints = 400000;
  let marks = [
    { value: 0, label: '0' },
    { value: 100000, label: '100k' },
    { value: 200000, label: '200k' },
    { value: 300000, label: '300k' },
    { value: maxPoints, label: '400k' },
  ];

  if (width < 900) {
    marks = [
      { value: 0, label: '0' },
      { value: 200000, label: '200k' },
      { value: maxPoints, label: '400k' },
    ];
  }

  // Query - Trip Tracker Points ?
  const path = '/homepage/getContestPoints';
  const queryKey = [path, { AgtNo }];
  const query = useQuery({
    enabled: isAgt(AgtNo),
    queryKey,
    queryFn: async () => {
      const response: {
        data: { contestPoints: number };
      } = await API.post('DashboardAPI', path, { body: { AgtNo } });

      return response.data;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Custom Slider Component
  const ContestSlider = styled(Slider)(() => ({
    '.MuiSlider-markLabel': {
      color: '#fff',
      fontSize: 12,
      letterSpacing: 3,
      fontFamily: 'Lato',
      padding: isMobile ? 5 : 10,
    },
    '.MuiSlider-valueLabel': {
      backgroundColor: '#17191B',
      fontFamily: 'Lato',
    },
    '.MuiSlider-rail': {
      background: `linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.6)),
      linear-gradient(180deg, rgba(23, 25, 27, 0) 0%, rgba(23, 25, 27, 0.5) 100%);`,
      height: isMobile ? 10 : 15,
    },
    '.MuiSlider-track': {
      background: `linear-gradient(90deg, ${uicolors.red[800]} 0%, ${uicolors.red[500]} 100%);`,
      height: 15,
    },
    '.MuiSlider-thumb': {
      height: 45,
      width: 45,
      backgroundColor: '#00000010',
      '&:before': {
        boxShadow: 'none',
      },
    },
  }));

  interface ContestThumbComponentProps extends React.HTMLAttributes<unknown> {}

  function ContestThumbComponent(props: ContestThumbComponentProps) {
    const { children, ...other } = props;

    let roosterIcon = <Rooster width={isMobile ? 30 : 35} />;
    if ((query.data && query.data.contestPoints >= 400000) || !isAgt(AgtNo)) {
      roosterIcon = <RoosterGold width={isMobile ? 30 : 35} />;
    }

    return (
      <SliderThumb {...other}>
        {children}
        {roosterIcon}
      </SliderThumb>
    );
  }

  return (
    <Box
      flex={1}
      py={{ xs: 1, sm: 2 }}
      sx={{
        ...homeStyles.box,
        border: 'none',
        background: 'none',
        boxShadow: 'none',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 4,
      }}
    >
      <Box sx={{ whiteSpace: 'nowrap', my: isMobile ? 0 : 1 }}>
        Portugal Trip Tracker
      </Box>
      <Box width="100%">
        <ContestSlider
          defaultValue={0}
          value={!isAgt(AgtNo) ? maxPoints : query.data?.contestPoints || 0}
          step={null}
          valueLabelDisplay="auto"
          valueLabelFormat={(i) => {
            return i.toLocaleString();
          }}
          marks={marks}
          min={0}
          max={maxPoints}
          slots={{ thumb: ContestThumbComponent }}
        />
      </Box>
    </Box>
  );
}
